<script setup lang="ts">
    import App from '@/App.ce.vue';
    import '@/assets/styles/main.scss';
    import { WIDGET_NUMERIC_DEFAULTS } from '@/constants/defaults';
    import { WidgetPositionEnum } from '@/constants/style';
    import { i18n, setI18nLanguage } from '@/i18n';
    import dayjs from 'dayjs';
    import 'dayjs/locale/en';
    import 'dayjs/locale/nl';
    import { type Composer } from 'vue-i18n';
    import { type WidgetProps } from './typings/widget';

    const props = withDefaults(defineProps<WidgetProps>(), {
        buttonLabel: '',
        buttonLabelClose: '',
        locale: 'nl',
        position: WidgetPositionEnum.BOTTOM_RIGHT,
        offsetX: 0,
        offsetY: 0,
        brandColor: '',
        minGuestAmount: WIDGET_NUMERIC_DEFAULTS.minGuestAmount,
        maxGuestAmount: WIDGET_NUMERIC_DEFAULTS.maxGuestAmount,
        guestAmountStep: WIDGET_NUMERIC_DEFAULTS.guestAmountStep,
        maxBudgetAmount: WIDGET_NUMERIC_DEFAULTS.maxBudgetAmount,
        minBudgetAmount: WIDGET_NUMERIC_DEFAULTS.minBudgetAmount,
        budgetStep: WIDGET_NUMERIC_DEFAULTS.budgetStep,
    });

    /*
     * Custom-element number attributes are coerced with `Number()`, and `Number('')` is 0
     * (not NaN). So a blank attribute such as max-guest-amount="" arrives as the number 0
     * rather than `undefined`, and `withDefaults` never replaces it. A max/step of 0 breaks
     * the range sliders, so treat any non-finite or non-positive value as "not provided" and
     * fall back to the configured default. Min values legitimately allow 0, so they only fall
     * back when the value is negative or non-finite.
     */
    const resolveNumber = (value: unknown, fallback: number, allowZero = false): number => {
        const parsed = Number(value);
        if (!Number.isFinite(parsed) || parsed < 0) return fallback;
        if (parsed === 0 && !allowZero) return fallback;
        return parsed;
    };

    const resolvedNumericProps = computed(() => ({
        minGuestAmount: resolveNumber(props.minGuestAmount, WIDGET_NUMERIC_DEFAULTS.minGuestAmount, true),
        maxGuestAmount: resolveNumber(props.maxGuestAmount, WIDGET_NUMERIC_DEFAULTS.maxGuestAmount),
        guestAmountStep: resolveNumber(props.guestAmountStep, WIDGET_NUMERIC_DEFAULTS.guestAmountStep),
        minBudgetAmount: resolveNumber(props.minBudgetAmount, WIDGET_NUMERIC_DEFAULTS.minBudgetAmount, true),
        maxBudgetAmount: resolveNumber(props.maxBudgetAmount, WIDGET_NUMERIC_DEFAULTS.maxBudgetAmount),
        budgetStep: resolveNumber(props.budgetStep, WIDGET_NUMERIC_DEFAULTS.budgetStep),
    }));

    provide<WidgetProps>('widgetProps', props);

    const i18nComposer = (i18n.global as unknown) as Composer;

    /*
     * Keep dayjs in sync with the active i18n locale. Both sources that can change the
     * language - the web component `locale` prop and the venue settings - end up writing
     * to the i18n composer locale, so watching it here updates dayjs for every source.
     */
    watch(
        () => i18nComposer.locale.value,
        activeLocale => {
            // eslint-disable-next-line import/no-named-as-default-member
            dayjs.locale(activeLocale ?? 'nl');
        },
        { immediate: true },
    );

    onMounted(() => {
        if (!props.venue) {
            // eslint-disable-next-line no-console
            console.error('Constell: Venue ID is required to render the widget');
        }
    });

    onUpdated(() => {
        // This should make sure that when the prop on the web component changes, we also update the i18n instance with the latest locale info.
        setI18nLanguage(i18n, props.locale);
    });

    /*
     * Register the widget fonts on the host document. An @font-face declared inside a
     * Shadow DOM is ignored by browsers, so we inject the Google Fonts stylesheet (and
     * its preconnects) into <head>, which makes Space Grotesk + Cormorant Infant
     * available to the shadow-rendered widget. Guarded by an id so multiple widget
     * instances on the same page don't duplicate the tags.
     */
    const injectWidgetFonts = (): void => {
        if (document.getElementById('constell-widget-fonts')) return;

        const preconnectGoogle = document.createElement('link');
        preconnectGoogle.rel = 'preconnect';
        preconnectGoogle.href = 'https://fonts.googleapis.com';

        const preconnectStatic = document.createElement('link');
        preconnectStatic.rel = 'preconnect';
        preconnectStatic.href = 'https://fonts.gstatic.com';
        preconnectStatic.crossOrigin = 'anonymous';

        const stylesheet = document.createElement('link');
        stylesheet.id = 'constell-widget-fonts';
        stylesheet.rel = 'stylesheet';
        stylesheet.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Infant:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,500;1,600;1,700&family=Space+Grotesk:wght@300..700&display=swap';

        document.head.append(preconnectGoogle, preconnectStatic, stylesheet);
    };

    onBeforeMount(() => {
        // First, make sure we pass our locale to the i18n instance (the watch above syncs dayjs)
        setI18nLanguage(i18n, props.locale);

        injectWidgetFonts();
    });
</script>

<template lang="html">
    <div id="app-root">
        <App
            v-bind="props"
            :min-guest-amount="resolvedNumericProps.minGuestAmount"
            :max-guest-amount="resolvedNumericProps.maxGuestAmount"
            :guest-amount-step="resolvedNumericProps.guestAmountStep"
            :min-budget-amount="resolvedNumericProps.minBudgetAmount"
            :max-budget-amount="resolvedNumericProps.maxBudgetAmount"
            :budget-step="resolvedNumericProps.budgetStep"
        />
    </div>
</template>

<style lang="scss" src="./assets/styles/main.scss"></style>
